import React from 'react';
import { LogOut, Settings, Sun, Moon, User, Bell } from 'lucide-react';
import GroupSwitcher from '../common/GroupSwitcher';
import { SyncStatusBadge } from '../../hooks/useFirebaseOptimized';
import { BandsintownImportGate } from '../subscription/FeatureGates';

const ADMIN_EMAIL = 'medeirossethcode@gmail.com';

// Boply Logo Component - 3 overlapping boxes
const BoplyLogo = ({ size = 32 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: 0, 
        left: 0,
        opacity: 0.5 
      }}
    />
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.22, 
        left: size * 0.22,
        opacity: 0.75 
      }}
    />
    <div 
      className="absolute bg-blue-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.45, 
        left: size * 0.45 
      }}
    />
  </div>
);

const Header = ({
  user,
  isDemoMode,
  currentPage,
  darkMode,
  showSettingsMenu,
  setShowSettingsMenu,
  syncStatus,
  connected,
  onlineMembers,
  // Actions
  setCurrentPage,
  setEditProfileModalOpen,
  setDashboardEditing,
  setBandsintownModalOpen,
  setFeedbackModalOpen,
  setShowGuidedTour,
  setNotificationSettingsOpen, // NEW: Add this prop
  toggleDarkMode,
  handleLogout,
  handleExitDemo,
}) => {
  return (
    <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        {/* Left side - Logo & Group */}
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => setCurrentPage('dashboard')}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity group"
          >
            <BoplyLogo size={32} />
            <span className="hidden md:inline text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Boply
            </span>
          </button>
          <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 hidden md:block" />
          
          {/* Show Demo indicator or Group Switcher */}
          {isDemoMode ? (
            <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-sm font-bold rounded-full">
              Demo Band
            </span>
          ) : (
            <GroupSwitcher />
          )}
        </div>

        {/* Right side - Status & Actions */}
        <div className="flex items-center gap-1.5 md:gap-2">
          <SyncStatusBadge
            syncStatus={syncStatus}
            connected={connected}
            onlineMembers={onlineMembers}
          />

          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">
              {user.email}
            </span>
          </div>

          {/* Settings Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              className={`p-2.5 rounded-xl transition-all duration-200 ${
                showSettingsMenu 
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title="Settings"
            >
              <Settings size={20} className={showSettingsMenu ? 'rotate-90 transition-transform duration-300' : 'transition-transform duration-300'} />
            </button>

            {showSettingsMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowSettingsMenu(false)}
                />
                <div className="absolute right-0 top-full mt-3 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl shadow-gray-200/50 dark:shadow-black/30 border border-gray-200/80 dark:border-gray-700/80 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* User Info Section */}
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800 dark:to-gray-900/50 border-b border-gray-200/80 dark:border-gray-700/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-purple-500/20">
                        {user.email?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{user.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Band Manager</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    {/* Edit Profile */}
                    <button
                      onClick={() => {
                        setEditProfileModalOpen(true);
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <User size={18} className="text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Edit Profile</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Update display name</p>
                      </div>
                    </button>

                    {/* Customize Dashboard */}
                    {currentPage === 'dashboard' && (
                      <button
                        onClick={() => {
                          setDashboardEditing(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-lg">🎨</span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">Customize Dashboard</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Reorder widgets</p>
                        </div>
                      </button>
                    )}

                    {/* Band Settings */}
                    <button
                      onClick={() => {
                        if (isDemoMode) {
                          handleExitDemo();
                        } else {
                          setCurrentPage('group-settings');
                        }
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Settings size={18} className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Band Settings</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Members & invites</p>
                      </div>
                    </button>

                    {/* Push Notifications - NEW */}
                    <button
                      onClick={() => {
                        setNotificationSettingsOpen(true);
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bell size={18} className="text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Push Notifications</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Get notified of new shows</p>
                      </div>
                    </button>

                    {/* Bandsintown Import */}
                    <BandsintownImportGate>
                      <button
                        onClick={() => {
                          setBandsintownModalOpen(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-lg">🎵</span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">Import from Bandsintown</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Sync your gig dates</p>
                        </div>
                      </button>
                    </BandsintownImportGate>

                    {/* Send Feedback */}
                    <button
                      onClick={() => {
                        setFeedbackModalOpen(true);
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-lg">💬</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Send Feedback</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Help us improve Boply</p>
                      </div>
                    </button>

                    {/* View Tutorial */}
                    <button
                      onClick={() => {
                        setShowGuidedTour(true);
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-lg">🎓</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">View Tutorial</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Take the tour again</p>
                      </div>
                    </button>

                    {/* Admin: View Feedback */}
                    {user.email === ADMIN_EMAIL && (
                      <button
                        onClick={() => {
                          setCurrentPage('feedback-admin');
                          setShowSettingsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-lg">📊</span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">View Feedback</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Admin only</p>
                        </div>
                      </button>
                    )}
                  </div>

                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent mx-4" />

                  <div className="p-2">
                    {/* Dark Mode Toggle */}
                    <button
                      onClick={() => {
                        toggleDarkMode();
                        setShowSettingsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        {darkMode ? <Sun size={18} className="text-amber-500" /> : <Moon size={18} className="text-indigo-500" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{darkMode ? 'Light Mode' : 'Dark Mode'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Switch appearance</p>
                      </div>
                    </button>

                    {/* Log Out */}
                    <button
                      onClick={() => {
                        setShowSettingsMenu(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <LogOut size={18} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Log Out</p>
                        <p className="text-xs text-red-500/70 dark:text-red-400/70">Sign out of your account</p>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;